/**
 * HouseholdRMD — Two-level RMD tracking on the Tax tab.
 *
 * Level 1: Summary dashboard — all RMD profiles with status bars and deadlines.
 * Level 2: Per-account detail — metrics, distribution tracking, and logging.
 *
 * Phase 2: Own IRA + Inherited IRA support.
 */

import { useContext, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  Modal,
  ProgressBar,
  Row,
  Table,
} from "react-bootstrap";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  extractServerMessage,
  useAuthenticatedFetch,
  useAuthenticatedMutationNew,
} from "../../lib/api";
import { formatCurrency } from "../../lib/numbers";
import { isRmdEligibleAccountType } from "../../lib/tax-registration";
import { contactUpdateBody } from "../contact/lib";
import { HouseholdContext } from "./HouseholdInfo";
import {
  rmdAnnualsPath,
  rmdProfilesPath,
  useQueryRmdAnnuals,
  useQueryRmdProfiles,
} from "./lib";
import { NotificationContext } from "../../Notifications";
import type { Contact, EditablePerson } from "../../../../api/src/contacts/lib";
import type {
  RMDProfile,
  RMDAnnual,
  RMDLedgerEntry,
  RMDLedgerSummary,
  CreateRMDProfile,
  CreateRMDAnnual,
  CreateRMDDistribution,
} from "../../../../api/src/rmd/rmd-lib";
import type { WrappedApiResponse } from "../../../../api/src/lib";
import {
  determineRule,
  ageInYear,
  getRuleDisplayLabel,
  getQCDLimit,
  isRuleResult,
} from "./rmd-helpers";
import RMDLedger, {
  detectDuplicates,
  DuplicateWarningBanner,
} from "./RMDLedger";

const CY = new Date().getFullYear();

// ── Helpers ──

function formatCurrencyValue(n: number): string {
  return formatCurrency(n, 2);
}

// ══════════════════════════════════════════
// TOP-LEVEL: Summary + Detail router
// ══════════════════════════════════════════

const HouseholdRMD = () => {
  const [selectedProfileId, setSelectedProfileId] = useState<number | null>(
    null,
  );

  if (selectedProfileId !== null) {
    return (
      <RMDAccountDetail
        profileId={selectedProfileId}
        onBack={() => setSelectedProfileId(null)}
      />
    );
  }

  return <RMDSummaryDashboard onSelectProfile={setSelectedProfileId} />;
};

export default HouseholdRMD;

// ══════════════════════════════════════════
// LEVEL 1: Summary Dashboard
// ══════════════════════════════════════════

const RMDSummaryDashboard = ({
  onSelectProfile,
}: {
  onSelectProfile: (id: number) => void;
}) => {
  const { household } = useContext(HouseholdContext);
  const { pushNotification } = useContext(NotificationContext);
  const { householdId } = useParams();
  const queryClient = useQueryClient();

  // Every page of `GET .../rmd/profiles` (APP-1653).
  const profilesUrl = rmdProfilesPath(householdId ?? "");
  const { data: profiles, isPending } = useQueryRmdProfiles(householdId ?? "", {
    enabled: householdId !== undefined,
  });

  const [showAdd, setShowAdd] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const createProfileMut = useAuthenticatedMutationNew<
    WrappedApiResponse<RMDProfile>,
    void,
    CreateRMDProfile
  >({
    // `POST /v1/households/:householdId/rmd/profiles` (APP-1653). The key is
    // unchanged; the url is under `/v1`.
    mutationKey: ["POST", "households", householdId ?? "", "rmd", "profiles"],
    urlBuilderFn: (key) => `/v1/${key.join("/")}`,
    mutationFn: () => {},
  });

  const members = useMemo(
    () =>
      ((household as any)?.members ?? []).filter(
        (m: Contact) => m.type === "person",
      ),
    [(household as any)?.members],
  );

  // Only RMD-eligible account types should appear in the Add RMD Account
  // dropdown — Traditional IRA, SEP IRA, SIMPLE IRA, Traditional 401k,
  // 403b, 457b, and Inherited variants. Roth IRA, Roth 401k, taxable
  // brokerage, and Trust accounts are explicitly excluded.
  const accounts = useMemo(
    () =>
      (household?.accounts ?? []).filter((a) =>
        isRmdEligibleAccountType(a.type),
      ),
    [household?.accounts],
  );

  const aggregateStats = useMemo(() => {
    if (!profiles?.length) return null;
    return {
      totalProfiles: profiles.length,
      ownIRA: profiles.filter((p) => !p.isInherited).length,
      inherited: profiles.filter((p) => p.isInherited).length,
    };
  }, [profiles]);

  if (isPending) return <Alert variant="info">Loading RMD profiles...</Alert>;

  const handleCreateProfile = async (data: CreateRMDProfile) => {
    try {
      setAddError(null);
      await createProfileMut.mutateAsync(data);
      queryClient.invalidateQueries({ queryKey: [profilesUrl] });
      setShowAdd(false);
      pushNotification({
        id: "rmd-profile-created",
        header: "RMD Profile Created",
        body: "New RMD account has been added",
        variant: "success",
      });
    } catch (err) {
      // Extract the server's specific message (e.g. the 409 conflict body)
      // so the Add RMD modal can render it inline near the Account field.
      // Falls back to the generic toast for non-specific errors.
      const specificMessage = extractServerMessage(err);
      if (specificMessage) {
        setAddError(specificMessage);
        return;
      }
      pushNotification({
        id: "rmd-profile-error",
        header: "Error",
        body: "Failed to create RMD profile",
        variant: "danger",
      });
    }
  };

  return (
    <>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-0">Required Minimum Distributions</h5>
          <small className="text-muted">
            Track RMDs across all retirement accounts for this household
          </small>
        </div>
        <Button variant="primary" size="sm" onClick={() => setShowAdd(true)}>
          + Add RMD Account
        </Button>
      </div>

      {/* Empty state */}
      {(!profiles || profiles.length === 0) && (
        <Card className="text-center py-5">
          <Card.Body>
            <h6>No RMD accounts configured yet</h6>
            <p className="text-muted mb-3">
              Add a Traditional IRA, 401(k), or Inherited IRA to begin tracking
              required distributions, QCDs, and withholding.
            </p>
            <Button variant="primary" onClick={() => setShowAdd(true)}>
              + Add First RMD Account
            </Button>
          </Card.Body>
        </Card>
      )}

      {/* Summary cards */}
      {profiles && profiles.length > 0 && (
        <>
          <Row className="mb-3">
            <Col md={3}>
              <Card className="text-center">
                <Card.Body className="py-2">
                  <small className="text-muted d-block">Total Accounts</small>
                  <h4 className="mb-0">{profiles.length}</h4>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <Card.Body className="py-2">
                  <small className="text-muted d-block">Own IRA / 401(k)</small>
                  <h4 className="mb-0">{aggregateStats?.ownIRA ?? 0}</h4>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <Card.Body className="py-2">
                  <small className="text-muted d-block">Inherited IRA</small>
                  <h4 className="mb-0 text-warning">
                    {aggregateStats?.inherited ?? 0}
                  </h4>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <Card.Body className="py-2">
                  <small className="text-muted d-block">Year</small>
                  <h4 className="mb-0">{CY}</h4>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Profile list */}
          <Card>
            <Table hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Owner</th>
                  <th>Type</th>
                  <th>Rule</th>
                  <th className="text-end">Required</th>
                  <th className="text-end">Distributed</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {profiles.map((profile) => (
                  <RMDSummaryRow
                    key={profile.id}
                    profile={profile}
                    annuals={profile.annuals ?? []}
                    members={members}
                    onClick={() => onSelectProfile(profile.id)}
                  />
                ))}
              </tbody>
            </Table>
          </Card>
        </>
      )}

      {/* Add Profile Modal */}
      <AddProfileModal
        show={showAdd}
        onHide={() => {
          setShowAdd(false);
          setAddError(null);
        }}
        members={members}
        accounts={accounts}
        onSave={handleCreateProfile}
        errorMessage={addError}
      />
    </>
  );
};

// ── Summary Row ──
const RMDSummaryRow = ({
  profile,
  annuals,
  onClick,
}: {
  profile: RMDProfile;
  annuals: RMDAnnual[];
  members: Contact[];
  onClick: () => void;
}) => {
  const currentAnnual = annuals.find((a) => a.year === CY);

  const ownerName = profile.ownerName || "Unknown";

  const ruleResult = determineRule({
    isInherited: profile.isInherited,
    ownerBirthYear: profile.ownerBirthYear,
    spouseIsSoleBeneficiary: profile.spouseIsSoleBeneficiary,
    spouseBirthYear: profile.spouseBirthYear ?? null,
    originalOwnerBirthYear: profile.originalOwnerBirthYear ?? null,
    originalOwnerDeathYear: profile.originalOwnerDeathYear ?? null,
    beneficiaryType: (profile.beneficiaryType as any) ?? null,
    spouseElection: (profile.spouseElection as any) ?? null,
    beneficiaryBirthYear: null,
  });

  const ruleLabel = getRuleDisplayLabel(ruleResult);

  const req = currentAnnual?.requiredAmount ?? 0;
  const dist = currentAnnual?.distributedAmount ?? 0;
  const pct = req > 0 ? Math.min(dist / req, 1) : 0;

  const statusBadge = !currentAnnual
    ? { bg: "secondary" as const, label: "No data" }
    : currentAnnual.status === "satisfied"
      ? { bg: "success" as const, label: "Satisfied" }
      : currentAnnual.status === "partial"
        ? { bg: "warning" as const, label: "Partial" }
        : currentAnnual.status === "none_required"
          ? { bg: "info" as const, label: "None Req." }
          : { bg: "danger" as const, label: "Pending" };

  return (
    <tr style={{ cursor: "pointer" }} onClick={onClick}>
      <td>
        <strong>{profile.accountLabel || "Untitled Account"}</strong>
      </td>
      <td>{ownerName}</td>
      <td>
        <Badge
          bg={profile.isInherited ? "warning" : "primary"}
          className={profile.isInherited ? "text-dark" : ""}
        >
          {profile.isInherited ? "Inherited" : "Own IRA"}
        </Badge>
        {profile.usesQcd && (
          <Badge bg="info" className="ms-1">
            QCD
          </Badge>
        )}
      </td>
      <td>
        <small>{ruleLabel}</small>
      </td>
      <td className="text-end">
        {req > 0 ? formatCurrencyValue(req) : "\u2014"}
      </td>
      <td className="text-end">
        {dist > 0 ? formatCurrencyValue(dist) : "\u2014"}
        {req > 0 && (
          <ProgressBar
            now={pct * 100}
            variant={pct >= 1 ? "success" : pct > 0.5 ? "info" : "warning"}
            style={{ height: 4, marginTop: 4 }}
          />
        )}
      </td>
      <td>
        <Badge bg={statusBadge.bg}>{statusBadge.label}</Badge>
      </td>
      <td className="text-end">
        <Button
          variant="primary"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
        >
          View
        </Button>
      </td>
    </tr>
  );
};

// ══════════════════════════════════════════
// LEVEL 2: Per-Account Detail
// ══════════════════════════════════════════

const RMDAccountDetail = ({
  profileId,
  onBack,
}: {
  profileId: number;
  onBack: () => void;
}) => {
  const { householdId } = useParams();
  // Every page of `GET .../rmd/profiles` (APP-1653).
  const { data: profiles, isPending } = useQueryRmdProfiles(householdId ?? "", {
    enabled: householdId !== undefined,
  });

  if (isPending) return <Alert variant="info">Loading profile…</Alert>;

  const profile = profiles?.find((p) => p.id === profileId);
  if (!profile) return <Alert variant="warning">Profile not found</Alert>;

  return <RMDAccountDetailLoaded profile={profile} onBack={onBack} />;
};

// State lives in the inner component so that `profile` is defined when
// `useState` runs. `useState`'s initializer fires only once, so reading
// `profile?.accountId` while the parent fetch is still pending would lock
// `activeTab` to "tracking" and never revisit it.
const RMDAccountDetailLoaded = ({
  profile,
  onBack,
}: {
  profile: RMDProfile;
  onBack: () => void;
}) => {
  const profileId = profile.id;
  const { pushNotification } = useContext(NotificationContext);
  const { householdId } = useParams();
  const queryClient = useQueryClient();

  const profileUrl = rmdProfilesPath(householdId ?? "");

  // Every page of `GET .../rmd/profiles/:profileId/annuals` (APP-1653).
  const annualsUrl = rmdAnnualsPath(householdId ?? "", profileId);
  const { data: annuals } = useQueryRmdAnnuals(householdId ?? "", profileId, {
    enabled: householdId !== undefined,
  });
  const currentAnnual = annuals?.find((a) => a.year === CY);

  // Fetch ledger entries for duplicate detection on the tracking tab
  const hasLedger = !!(profile.accountId && currentAnnual);
  const ledgerUrl = hasLedger
    ? `/v1/households/${householdId}/rmd/profiles/${profileId}/annuals/${currentAnnual!.id}/ledger`
    : `/v1/households/${householdId}/rmd/profiles/${profileId}/annuals/0/ledger`;
  const { data: ledgerResponse } = useAuthenticatedFetch<
    WrappedApiResponse<{
      entries: RMDLedgerEntry[];
      summary: RMDLedgerSummary;
    }>
  >(ledgerUrl, {}, { enabled: hasLedger });
  const ledgerEntries = ledgerResponse?.data?.entries ?? [];
  const trackingDuplicates =
    currentAnnual && ledgerEntries.length > 0
      ? detectDuplicates(currentAnnual.distributions, ledgerEntries)
      : [];

  const addDistMut = useAuthenticatedMutationNew<
    WrappedApiResponse<unknown>,
    unknown,
    CreateRMDDistribution
  >({
    // `POST /v1/.../rmd/distributions` (APP-1653). The key is unchanged; the
    // url is under `/v1`.
    mutationKey: [
      "POST",
      "households",
      householdId ?? "",
      "rmd",
      "distributions",
    ],
    urlBuilderFn: (key) => `/v1/${key.join("/")}`,
    mutationFn: ({ data }) => data,
  });

  const upsertAnnualMut = useAuthenticatedMutationNew<
    WrappedApiResponse<unknown>,
    unknown,
    CreateRMDAnnual
  >({
    // `POST /v1/.../rmd/annuals` (APP-1653). The key is unchanged; the url is
    // under `/v1`.
    mutationKey: ["POST", "households", householdId ?? "", "rmd", "annuals"],
    urlBuilderFn: (key) => `/v1/${key.join("/")}`,
    mutationFn: ({ data }) => data,
  });

  // The DELETE target is captured into state when the delete modal opens
  // rather than read from `currentAnnual` at mutate-time, so a profile
  // switch or query invalidation mid-flight can't retarget the request.
  const [showLogDist, setShowLogDist] = useState(false);
  const [editingBalance, setEditingBalance] = useState(false);
  const [showDeleteAnnual, setShowDeleteAnnual] = useState(false);
  const [deletingAnnualId, setDeletingAnnualId] = useState<number | null>(null);

  const deleteAnnualMut = useAuthenticatedMutationNew<
    WrappedApiResponse<unknown>,
    unknown,
    void
  >({
    mutationKey: [
      "DELETE",
      "households",
      householdId ?? "",
      "rmd",
      "annuals",
      String(deletingAnnualId ?? 0),
    ],
    // `DELETE /v1/.../rmd/annuals/:annualId` (APP-1653). The key is
    // unchanged; the url is under `/v1`.
    urlBuilderFn: (key) => `/v1/${key.join("/")}`,
    mutationFn: () => {},
  });
  // For profiles with a linked account, the auto-imported ledger is the
  // primary view; manual logging is a fallback for unlinked profiles.
  const [activeTab, setActiveTab] = useState<
    "tracking" | "ledger" | "projection" | "planner"
  >(profile.accountId ? "ledger" : "tracking");

  const ownerName = profile.ownerName || "Unknown";

  const ruleResult = determineRule({
    isInherited: profile.isInherited,
    ownerBirthYear: profile.ownerBirthYear,
    spouseIsSoleBeneficiary: profile.spouseIsSoleBeneficiary,
    spouseBirthYear: profile.spouseBirthYear ?? null,
    originalOwnerBirthYear: profile.originalOwnerBirthYear ?? null,
    originalOwnerDeathYear: profile.originalOwnerDeathYear ?? null,
    beneficiaryType: (profile.beneficiaryType as any) ?? null,
    spouseElection: (profile.spouseElection as any) ?? null,
    beneficiaryBirthYear: null,
  });

  const ruleDescription = isRuleResult(ruleResult)
    ? ruleResult.description
    : ruleResult.error;

  const ownerAge =
    profile.ownerBirthYear != null
      ? ageInYear(profile.ownerBirthYear, CY)
      : null;

  // Profile-level settings surfaced on the detail view so advisors can verify
  // stored QCD, growth, and withholding defaults without opening the edit form.
  const growthPct = `${(profile.growthAssumption * 100).toFixed(1)}%`;
  const fmtRatePct = (r: number | null): string =>
    r == null ? "\u2014" : `${(r * 100).toFixed(r * 100 >= 10 ? 0 : 1)}%`;
  const withholdingLabel =
    profile.defaultWithholdingRateFed == null &&
    profile.defaultWithholdingRateState == null
      ? "— / —"
      : `Fed ${fmtRatePct(profile.defaultWithholdingRateFed)} / State ${fmtRatePct(profile.defaultWithholdingRateState)}`;

  // Deadline info — compute whole days between two calendar dates
  // rather than millisecond timestamps. `new Date("2026-12-31")` parses
  // as UTC midnight, so a naive `getTime() - Date.now()` diff goes
  // negative during the advisor's local 12/31 evening once the UTC day
  // has rolled over. Parse both sides as YYYY-MM-DD and compare via
  // Date.UTC to get a stable, DST-safe day count.
  const deadlineYmd = currentAnnual?.deadline
    ? String(currentAnnual.deadline).slice(0, 10)
    : `${CY}-12-31`;
  const todayYmd = new Date().toLocaleDateString("en-CA");
  const [dY, dM, dD] = deadlineYmd.split("-").map(Number);
  const [tY, tM, tD] = todayYmd.split("-").map(Number);
  const daysLeft = Math.round(
    (Date.UTC(dY, dM - 1, dD) - Date.UTC(tY, tM - 1, tD)) /
      (1000 * 60 * 60 * 24),
  );

  const pctComplete =
    currentAnnual && currentAnnual.requiredAmount > 0
      ? Math.min(
          currentAnnual.distributedAmount / currentAnnual.requiredAmount,
          1,
        )
      : 0;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [annualsUrl] });
    queryClient.invalidateQueries({ queryKey: [profileUrl] });
  };

  const handleUpsertAnnual = async (data: CreateRMDAnnual) => {
    try {
      await upsertAnnualMut.mutateAsync(data);
      invalidate();
      setEditingBalance(false);
      pushNotification({
        id: "rmd-annual-saved",
        header: "RMD Annual Record",
        body: `${CY} RMD calculated`,
        variant: "success",
      });
    } catch {
      pushNotification({
        id: "rmd-annual-error",
        header: "Error",
        body: "Failed to save annual record",
        variant: "danger",
      });
    }
  };

  const handleDeleteAnnual = async () => {
    if (deletingAnnualId == null) return;
    try {
      await deleteAnnualMut.mutateAsync();
      // Logged distributions and reconciled ledger entries cascade-delete on
      // the server, so drop the cached ledger payload along with the profile
      // and annual queries to prevent stale Remaining/Distributed totals.
      queryClient.invalidateQueries({ queryKey: [ledgerUrl] });
      invalidate();
      setShowDeleteAnnual(false);
      setDeletingAnnualId(null);
      pushNotification({
        id: "rmd-annual-deleted",
        header: "RMD Annual Record Deleted",
        body: `${CY} annual record removed`,
        variant: "success",
      });
    } catch {
      pushNotification({
        id: "rmd-annual-delete-error",
        header: "Error",
        body: "Failed to delete annual record",
        variant: "danger",
      });
    }
  };

  const handleLogDistribution = async (data: CreateRMDDistribution) => {
    try {
      await addDistMut.mutateAsync(data);
      invalidate();
      setShowLogDist(false);
      pushNotification({
        id: "rmd-dist-logged",
        header: "Distribution Logged",
        body: "Distribution has been recorded",
        variant: "success",
      });
    } catch {
      pushNotification({
        id: "rmd-dist-error",
        header: "Error",
        body: "Failed to log distribution",
        variant: "danger",
      });
    }
  };

  return (
    <>
      {/* Back button + header */}
      <div className="d-flex align-items-center mb-3">
        <Button variant="link" className="p-0 me-3" onClick={onBack}>
          &larr; All Accounts
        </Button>
        <div className="flex-grow-1">
          <h5 className="mb-0">
            {profile.accountLabel || "RMD Account"}
            <Badge
              bg={profile.isInherited ? "warning" : "primary"}
              className={`ms-2 ${profile.isInherited ? "text-dark" : ""}`}
            >
              {profile.isInherited ? "Inherited IRA" : "Own IRA"}
            </Badge>
            {profile.usesQcd && (
              <Badge bg="info" className="ms-1">
                QCD
              </Badge>
            )}
          </h5>
          <small className="text-muted d-block">
            {ownerName} &middot; Age {ownerAge ?? "—"} &middot;{" "}
            {ruleDescription}
          </small>
          <small className="text-muted">
            QCD: {profile.usesQcd ? "Enabled" : "Disabled"} &middot; Growth:{" "}
            {growthPct} &middot; Default withholding: {withholdingLabel}
          </small>
        </div>
        {daysLeft > 0 &&
          currentAnnual?.status !== "satisfied" &&
          currentAnnual?.status !== "none_required" && (
            <Badge
              bg={
                daysLeft < 30
                  ? "danger"
                  : daysLeft < 90
                    ? "warning"
                    : "secondary"
              }
              className="fs-6 me-2"
            >
              {daysLeft} days left
            </Badge>
          )}
        {currentAnnual && (
          <Dropdown align="end">
            <Dropdown.Toggle
              variant="outline-secondary"
              size="sm"
              id={`rmd-annual-actions-${profile.id}`}
            >
              Actions
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={() => setEditingBalance(true)}
                disabled={editingBalance}
              >
                Edit prior year-end balance
              </Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item
                onClick={() => {
                  if (!currentAnnual) return;
                  setDeletingAnnualId(currentAnnual.id);
                  setShowDeleteAnnual(true);
                }}
                className="text-danger"
              >
                Delete {CY} annual record
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        )}
      </div>

      {/* Inline edit form — takes over from the key metrics row until the
          advisor saves or cancels. Pre-fills with the current balance so
          correcting a typo only requires editing the last few digits. */}
      {currentAnnual && editingBalance && (
        <Card className="mb-3">
          <Card.Body>
            <h6 className="mb-0">Edit {CY} Prior Year-End Balance</h6>
            <small className="text-muted">
              Updating the balance recalculates {CY}&apos;s divisor, required
              amount, and deadline. Previously logged distributions are
              preserved.
            </small>
            <SetBalanceForm
              profile={profile}
              initialBalance={currentAnnual.priorYearEndBalance}
              onSave={handleUpsertAnnual}
              onCancel={() => setEditingBalance(false)}
              saveLabel={`Recalculate ${CY} RMD`}
            />
          </Card.Body>
        </Card>
      )}

      {/* Key metrics row */}
      {currentAnnual && !editingBalance && (
        <Row className="mb-3">
          <Col md={2}>
            <Card className="text-center h-100">
              <Card.Body className="py-2">
                <small className="text-muted d-block">
                  Prior Yr Balance{" "}
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 align-baseline"
                    onClick={() => setEditingBalance(true)}
                    title="Edit prior year-end balance"
                  >
                    Edit
                  </Button>
                </small>
                <strong>
                  {formatCurrencyValue(currentAnnual.priorYearEndBalance)}
                </strong>
              </Card.Body>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="text-center h-100">
              <Card.Body className="py-2">
                <small className="text-muted d-block">Divisor</small>
                <strong>{currentAnnual.divisor ?? "\u2014"}</strong>
                <small className="text-muted d-block">
                  {currentAnnual.irsTableUsed?.replace(/_/g, " ") ?? ""}
                </small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="text-center h-100 border-primary">
              <Card.Body className="py-2">
                <small className="text-muted d-block">Required RMD</small>
                <strong className="text-primary">
                  {formatCurrencyValue(currentAnnual.requiredAmount)}
                </strong>
              </Card.Body>
            </Card>
          </Col>
          <Col md={2}>
            <Card
              className={`text-center h-100 ${currentAnnual.status === "satisfied" ? "border-success" : ""}`}
            >
              <Card.Body className="py-2">
                <small className="text-muted d-block">Distributed</small>
                <strong
                  className={
                    currentAnnual.status === "satisfied" ? "text-success" : ""
                  }
                >
                  {formatCurrencyValue(currentAnnual.distributedAmount)}
                </strong>
              </Card.Body>
            </Card>
          </Col>
          <Col md={2}>
            <Card
              className={`text-center h-100 ${currentAnnual.remainingAmount > 0 ? "border-danger" : ""}`}
            >
              <Card.Body className="py-2">
                <small className="text-muted d-block">Remaining</small>
                <strong
                  className={
                    currentAnnual.remainingAmount > 0
                      ? "text-danger"
                      : "text-success"
                  }
                >
                  {formatCurrencyValue(currentAnnual.remainingAmount)}
                </strong>
              </Card.Body>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="text-center h-100">
              <Card.Body className="py-2">
                <small className="text-muted d-block">QCD YTD</small>
                <strong>{formatCurrencyValue(currentAnnual.qcdAmount)}</strong>
                <small className="text-muted d-block">
                  / {formatCurrencyValue(getQCDLimit(CY))} limit
                </small>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Progress bar */}
      {currentAnnual && currentAnnual.requiredAmount > 0 && (
        <ProgressBar
          now={pctComplete * 100}
          variant={
            currentAnnual.status === "satisfied"
              ? "success"
              : pctComplete > 0.5
                ? "info"
                : "warning"
          }
          label={`${Math.round(pctComplete * 100)}% complete`}
          className="mb-3"
          style={{ height: 24 }}
        />
      )}

      {/* QCD limit warning */}
      {currentAnnual && currentAnnual.qcdAmount >= getQCDLimit(CY) * 0.9 && (
        <Alert variant="warning" className="mb-3">
          <strong>QCD Limit Warning:</strong> QCD year-to-date (
          {formatCurrencyValue(currentAnnual.qcdAmount)}) is at or above 90% of
          the {formatCurrencyValue(getQCDLimit(CY))} annual limit. Further
          qualified charitable distributions may exceed the IRS cap.
        </Alert>
      )}

      {/* Withholding adequacy alert */}
      {currentAnnual &&
        currentAnnual.distributedAmount > 0 &&
        currentAnnual.taxWithheldFed + currentAnnual.taxWithheldState > 0 &&
        (currentAnnual.taxWithheldFed + currentAnnual.taxWithheldState) /
          currentAnnual.distributedAmount <
          0.15 && (
          <Alert variant="info" className="mb-3">
            <strong>Withholding Check:</strong> Total withholding (
            {formatCurrencyValue(
              currentAnnual.taxWithheldFed + currentAnnual.taxWithheldState,
            )}
            ) is less than 15% of gross distributions (
            {formatCurrencyValue(currentAnnual.distributedAmount)}). Consider
            reviewing withholding rates to avoid potential underpayment
            penalties.
          </Alert>
        )}

      {/* Sub-tabs — Ledger first when the account is linked, since the
          auto-imported ledger is the primary view; Manual Log is fallback. */}
      <ul className="nav nav-pills mb-3">
        {(
          [
            ...(profile.accountId ? (["ledger"] as const) : []),
            "tracking",
            "projection",
            "planner",
          ] as const
        ).map((tab) => (
          <li className="nav-item" key={tab}>
            <button
              className={`nav-link ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === "tracking"
                ? "Manual Log"
                : tab === "ledger"
                  ? "Distribution Ledger"
                  : tab === "projection"
                    ? "Multi-Year Projection"
                    : "Distribution Planner"}
            </button>
          </li>
        ))}
      </ul>

      {/* Tab: Distribution Tracking */}
      {activeTab === "tracking" && (
        <Card>
          <Card.Body>
            <DuplicateWarningBanner duplicates={trackingDuplicates} />
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">{CY} Distributions</h6>
              <Button
                variant="primary"
                size="sm"
                onClick={() => setShowLogDist(true)}
                disabled={!currentAnnual}
              >
                + Log Distribution
              </Button>
            </div>

            {!currentAnnual && (
              <Alert variant="info">
                No {CY} annual record yet. Enter the 12/31/{CY - 1} balance to
                calculate this year&apos;s RMD.
                <SetBalanceForm profile={profile} onSave={handleUpsertAnnual} />
              </Alert>
            )}

            {currentAnnual && currentAnnual.distributions.length > 0 && (
              <Table size="sm" responsive>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th className="text-end">Gross</th>
                    <th className="text-end">Fed W/H</th>
                    <th className="text-end">State W/H</th>
                    <th className="text-end">Net</th>
                    <th>QCD</th>
                    <th>Notes</th>
                    <th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {currentAnnual.distributions.map((d) => (
                    <tr key={d.id}>
                      <td>
                        {new Date(d.distributionDate).toLocaleDateString(
                          undefined,
                          { timeZone: "UTC" },
                        )}
                      </td>
                      <td className="text-end">
                        {formatCurrencyValue(d.grossAmount)}
                      </td>
                      <td className="text-end">
                        {formatCurrencyValue(d.fedWithholding)}
                      </td>
                      <td className="text-end">
                        {formatCurrencyValue(d.stateWithholding)}
                      </td>
                      <td className="text-end">
                        {formatCurrencyValue(d.netAmount)}
                      </td>
                      <td>
                        {d.isQcd ? <Badge bg="info">QCD</Badge> : "\u2014"}
                      </td>
                      <td className="text-muted">{d.notes || "\u2014"}</td>
                      <td className="text-muted">
                        {d.sourceDescription || "\u2014"}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="fw-bold">
                    <td>Total</td>
                    <td className="text-end">
                      {formatCurrencyValue(currentAnnual.distributedAmount)}
                    </td>
                    <td className="text-end">
                      {formatCurrencyValue(currentAnnual.taxWithheldFed)}
                    </td>
                    <td className="text-end">
                      {formatCurrencyValue(currentAnnual.taxWithheldState)}
                    </td>
                    <td className="text-end">
                      {formatCurrencyValue(
                        currentAnnual.distributedAmount -
                          currentAnnual.taxWithheldFed -
                          currentAnnual.taxWithheldState,
                      )}
                    </td>
                    <td>{formatCurrencyValue(currentAnnual.qcdAmount)}</td>
                    <td></td>
                    <td></td>
                  </tr>
                </tfoot>
              </Table>
            )}

            {currentAnnual && currentAnnual.distributions.length === 0 && (
              <p className="text-muted text-center my-4">
                No distributions logged for {CY} yet. Click &quot;Log
                Distribution&quot; to record one.
              </p>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Tab: Distribution Ledger */}
      {activeTab === "ledger" &&
        householdId !== undefined &&
        currentAnnual &&
        profile.accountId && (
          <RMDLedger
            householdId={householdId}
            profileId={profile.id}
            annualId={currentAnnual.id}
            requiredAmount={currentAnnual.requiredAmount}
            manualDistributions={currentAnnual.distributions}
          />
        )}
      {activeTab === "ledger" && !currentAnnual && (
        <Card>
          <Card.Body>
            <Alert variant="info">
              Set the prior year-end balance first to enable the distribution
              ledger.
            </Alert>
          </Card.Body>
        </Card>
      )}

      {/* Tab: Multi-Year Projection */}
      {activeTab === "projection" && (
        <Card>
          <Card.Body>
            <Alert variant="info">
              Multi-year RMD projection is coming soon. This will show projected
              RMDs over the next 10+ years using the saved profile data and
              growth assumptions.
            </Alert>
          </Card.Body>
        </Card>
      )}

      {/* Tab: Distribution Planner */}
      {activeTab === "planner" && (
        <Card>
          <Card.Body>
            <Alert variant="info">
              Distribution planner is coming soon. This will help plan how to
              spread distributions across remaining years to optimize tax
              outcomes.
            </Alert>
          </Card.Body>
        </Card>
      )}

      {/* Log Distribution Modal */}
      <LogDistributionModal
        show={showLogDist}
        onHide={() => setShowLogDist(false)}
        annualId={currentAnnual?.id ?? null}
        onSave={handleLogDistribution}
      />

      {/* Delete Annual Confirmation */}
      <Modal
        show={showDeleteAnnual}
        onHide={() => {
          setShowDeleteAnnual(false);
          setDeletingAnnualId(null);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete {CY} Annual Record?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">
            This permanently removes the {CY} RMD annual record for{" "}
            <strong>{profile.accountLabel || "this account"}</strong>.
          </p>
          {currentAnnual && currentAnnual.distributions.length > 0 && (
            <Alert variant="warning" className="mb-2">
              <strong>
                {currentAnnual.distributions.length} logged distribution
                {currentAnnual.distributions.length === 1 ? "" : "s"}
              </strong>{" "}
              and any reconciled ledger entries will also be deleted. This
              cannot be undone.
            </Alert>
          )}
          <p className="text-muted mb-0" style={{ fontSize: "0.85rem" }}>
            Use this when the prior year-end balance was wrong or the account
            should not have been calculated yet. You can re-enter the balance
            afterward to start fresh.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowDeleteAnnual(false);
              setDeletingAnnualId(null);
            }}
            disabled={deleteAnnualMut.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteAnnual}
            disabled={deleteAnnualMut.isPending}
          >
            {deleteAnnualMut.isPending ? "Deleting..." : "Delete annual record"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

// ══════════════════════════════════════════
// Set Balance Form (inline, for creating annual record)
// ══════════════════════════════════════════

const SetBalanceForm = ({
  profile,
  initialBalance,
  onSave,
  onCancel,
  saveLabel,
}: {
  profile: RMDProfile;
  initialBalance?: number | null;
  onSave: (data: CreateRMDAnnual) => Promise<void>;
  onCancel?: () => void;
  saveLabel?: string;
}) => {
  const [balance, setBalance] = useState(
    initialBalance != null ? String(initialBalance) : "",
  );
  const [saving, setSaving] = useState(false);

  const handleCalc = async () => {
    const bal = parseFloat(balance.replace(/[^0-9.]/g, ""));
    if (!bal || bal <= 0) return;

    setSaving(true);
    try {
      await onSave({
        rmdProfileId: profile.id,
        year: CY,
        priorYearEndBalance: bal,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Row className="mt-3 align-items-end">
      <Col md={5}>
        <Form.Group>
          <Form.Label>12/31/{CY - 1} Account Balance</Form.Label>
          <Form.Control
            type="text"
            placeholder="e.g., 500,000"
            value={balance}
            onChange={(e) => setBalance(e.target.value)}
          />
        </Form.Group>
      </Col>
      <Col md={3}>
        <Button variant="primary" onClick={handleCalc} disabled={saving}>
          {saving ? "Saving..." : (saveLabel ?? `Calculate ${CY} RMD`)}
        </Button>
        {onCancel && (
          <Button
            variant="outline-secondary"
            className="ms-2"
            onClick={onCancel}
            disabled={saving}
          >
            Cancel
          </Button>
        )}
      </Col>
    </Row>
  );
};

// ══════════════════════════════════════════
// MODALS
// ══════════════════════════════════════════

const AddProfileModal = ({
  show,
  onHide,
  members,
  accounts,
  onSave,
  errorMessage,
}: {
  show: boolean;
  onHide: () => void;
  members: Contact[];
  accounts: any[];
  onSave: (data: CreateRMDProfile) => Promise<void>;
  errorMessage?: string | null;
}) => {
  const { pushNotification } = useContext(NotificationContext);
  const queryClient = useQueryClient();
  const { householdId } = useParams();

  // `growthAssumption` and the two withholding rates are held and sent on 0
  // to 100, as the user enters them: the `POST` body is on 0 to 100
  // (`CreateRMDProfileDto`, `api/src/rmd/rmd.dto.ts`).
  const [form, setForm] = useState<Partial<CreateRMDProfile>>({
    isInherited: false,
    accountLabel: "",
    usesQcd: false,
    growthAssumption: 5,
  });

  // Track manually-entered DOB for contacts missing it
  const [manualDob, setManualDob] = useState("");
  const [savingDob, setSavingDob] = useState(false);
  // Track whether the selected member has a DOB (for form validation only)
  const [memberBirthYear, setMemberBirthYear] = useState<number | null>(null);

  const selectedMember = members.find((m) => m.id === form.contactId);
  const memberHasDob = !!selectedMember?.birthDate;
  const memberNeedsDob = selectedMember && !memberHasDob;

  // Mutation to update the contact record with the missing DOB
  const updateContactMut = useAuthenticatedMutationNew<
    WrappedApiResponse<unknown>,
    unknown,
    EditablePerson
  >({
    // `PUT /v1/contacts/:id` (APP-1653). The key is unchanged;
    // the url is under `/v1`.
    mutationKey: ["PUT", "contacts", String(selectedMember?.id ?? 0)],
    urlBuilderFn: (key) => `/v1/${key.join("/")}`,
    mutationFn: ({ data }) => data,
  });

  const handleSaveDob = async () => {
    if (!selectedMember || !manualDob) return;
    setSavingDob(true);
    try {
      // The keys `UpdateContactDto` declares, of the member as the household
      // read gave it (APP-1653).
      await updateContactMut.mutateAsync(
        contactUpdateBody({
          ...selectedMember,
          birthDate: new Date(manualDob),
        }),
      );

      setMemberBirthYear(new Date(manualDob).getUTCFullYear());

      queryClient.invalidateQueries({
        queryKey: [`/households/${householdId}`],
      });

      pushNotification({
        id: "rmd-dob-saved",
        header: "Date of Birth Saved",
        body: `DOB saved for ${selectedMember.givenName || selectedMember.name}`,
        variant: "success",
      });
    } catch {
      pushNotification({
        id: "rmd-dob-error",
        header: "Error",
        body: "Failed to save date of birth",
        variant: "danger",
      });
    } finally {
      setSavingDob(false);
    }
  };

  const handleSave = () => {
    if (!form.contactId || !form.accountLabel) return;
    // Ensure the selected member has a DOB (either from CRM or just saved)
    if (!memberHasDob && !memberBirthYear) return;
    if (
      form.isInherited &&
      (!form.originalOwnerBirthYear || !form.originalOwnerDeathYear)
    )
      return;
    onSave(form as CreateRMDProfile);
  };

  const ratesInRange =
    (form.defaultWithholdingRateFed == null ||
      (form.defaultWithholdingRateFed >= 0 &&
        form.defaultWithholdingRateFed <= 100)) &&
    (form.defaultWithholdingRateState == null ||
      (form.defaultWithholdingRateState >= 0 &&
        form.defaultWithholdingRateState <= 100));

  const canSave = !!(
    form.contactId &&
    form.accountLabel &&
    (memberHasDob || memberBirthYear) &&
    ratesInRange &&
    (!form.isInherited ||
      (form.originalOwnerBirthYear && form.originalOwnerDeathYear))
  );

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Add RMD Account</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Household Member (Owner / Beneficiary)</Form.Label>
              <Form.Select
                onChange={(e) => {
                  const m = members.find(
                    (member) => member.id === +e.target.value,
                  );
                  setManualDob("");
                  setMemberBirthYear(
                    m?.birthDate
                      ? new Date(m.birthDate).getUTCFullYear()
                      : null,
                  );
                  setForm((f) => ({
                    ...f,
                    contactId: +e.target.value,
                  }));
                }}
              >
                <option value="">Select member...</option>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.givenName || m.name} {m.familyName || ""}{" "}
                    {m.title ? `(${m.title})` : ""}
                    {!m.birthDate ? " - No DOB" : ""}
                  </option>
                ))}
              </Form.Select>

              {selectedMember && memberHasDob && (
                <Form.Text className="text-success">
                  DOB:{" "}
                  {new Date(selectedMember.birthDate!).toLocaleDateString(
                    undefined,
                    { timeZone: "UTC" },
                  )}{" "}
                  &middot; Age:{" "}
                  {ageInYear(
                    new Date(selectedMember.birthDate!).getUTCFullYear(),
                    CY,
                  )}
                </Form.Text>
              )}
            </Form.Group>

            {/* DOB missing warning + inline entry */}
            {memberNeedsDob && (
              <Alert variant="warning" className="mt-2 mb-0 py-2">
                <strong>Date of birth is missing</strong>
                <p className="mb-2" style={{ fontSize: "0.85rem" }}>
                  {selectedMember.givenName || selectedMember.name} does not
                  have a date of birth in the CRM. RMD calculations require DOB.
                  Enter it below to save to their contact record.
                </p>
                <div className="d-flex align-items-end gap-2">
                  <Form.Group style={{ flex: 1 }}>
                    <Form.Label className="mb-1" style={{ fontSize: "0.8rem" }}>
                      Date of Birth
                    </Form.Label>
                    <Form.Control
                      type="date"
                      size="sm"
                      value={manualDob}
                      onChange={(e) => setManualDob(e.target.value)}
                    />
                  </Form.Group>
                  <Button
                    variant="warning"
                    size="sm"
                    disabled={!manualDob || savingDob}
                    onClick={handleSaveDob}
                    style={{ whiteSpace: "nowrap" }}
                  >
                    {savingDob ? "Saving..." : "Save DOB to CRM"}
                  </Button>
                </div>
                {memberBirthYear && (
                  <Form.Text className="text-success d-block mt-1">
                    Birth year: {memberBirthYear} &middot; Age:{" "}
                    {ageInYear(memberBirthYear, CY)}
                  </Form.Text>
                )}
              </Alert>
            )}
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Account</Form.Label>
              <Form.Select
                onChange={(e) => {
                  if (e.target.value === "__manual") {
                    setForm((f) => ({ ...f, accountId: null }));
                  } else {
                    const acct = accounts.find((a) => a.id === +e.target.value);
                    setForm((f) => ({
                      ...f,
                      accountId: acct?.id ?? null,
                      accountLabel: acct?.displayName ?? f.accountLabel,
                    }));
                  }
                }}
              >
                <option value="__manual">Enter manually</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.displayName} ({a.type || "Unknown type"})
                  </option>
                ))}
              </Form.Select>
              {errorMessage && (
                <Alert variant="danger" className="mt-2 mb-0 py-2">
                  {errorMessage}
                </Alert>
              )}
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Account Label</Form.Label>
              <Form.Control
                placeholder="e.g., Schwab Traditional IRA ****1234"
                value={form.accountLabel || ""}
                onChange={(e) =>
                  setForm((f) => ({ ...f, accountLabel: e.target.value }))
                }
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Account Type</Form.Label>
              <Form.Select
                value={form.isInherited ? "inherited" : "own"}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    isInherited: e.target.value === "inherited",
                    // Reset inherited-specific fields when switching back to own
                    ...(e.target.value === "own"
                      ? {
                          originalOwnerBirthYear: null,
                          originalOwnerDeathYear: null,
                          beneficiaryType: null,
                          spouseElection: null,
                        }
                      : {}),
                  }))
                }
              >
                <option value="own">Own IRA / 401(k)</option>
                <option value="inherited">Inherited IRA</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Uses QCD?</Form.Label>
              <Form.Select
                value={form.usesQcd ? "yes" : "no"}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    usesQcd: e.target.value === "yes",
                  }))
                }
              >
                <option value="no">No</option>
                <option value="yes">Yes (age 70 1/2+)</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {/* Own IRA fields */}
        {!form.isInherited && (
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Growth Assumption</Form.Label>
                <Form.Select
                  value={String(form.growthAssumption || 5)}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      growthAssumption: +e.target.value,
                    }))
                  }
                >
                  <option value="0">0%</option>
                  <option value="3">3%</option>
                  <option value="5">5%</option>
                  <option value="7">7%</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        )}

        {/* Inherited IRA fields */}
        {form.isInherited && (
          <>
            <hr />
            <h6>Original Owner &amp; Beneficiary Details</h6>
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Original Owner Birth Year</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="e.g., 1940"
                    min={1900}
                    max={CY}
                    value={form.originalOwnerBirthYear ?? ""}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        originalOwnerBirthYear: e.target.value
                          ? +e.target.value
                          : null,
                      }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Original Owner Death Year</Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="e.g., 2022"
                    min={1900}
                    max={CY}
                    value={form.originalOwnerDeathYear ?? ""}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        originalOwnerDeathYear: e.target.value
                          ? +e.target.value
                          : null,
                      }))
                    }
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Beneficiary Relationship</Form.Label>
                  <Form.Select
                    value={form.beneficiaryType || "non_spouse"}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        beneficiaryType: e.target.value,
                        // Reset spouse election when changing away from spouse
                        ...(e.target.value !== "spouse"
                          ? { spouseElection: null }
                          : {}),
                      }))
                    }
                  >
                    <option value="spouse">Spouse</option>
                    <option value="non_spouse">Non-Spouse Individual</option>
                    <option value="minor_child">Minor Child</option>
                    <option value="disabled">Disabled/Chronically Ill</option>
                    <option value="not_much_younger">
                      Not Much Younger (&le;10 yrs)
                    </option>
                    <option value="entity">
                      Entity (Estate/Trust/Charity)
                    </option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            {form.beneficiaryType === "spouse" && (
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Spouse Election</Form.Label>
                    <Form.Select
                      value={form.spouseElection || "treat_as_own"}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          spouseElection: e.target.value,
                        }))
                      }
                    >
                      <option value="treat_as_own">Treat as Own IRA</option>
                      <option value="remain">Remain as Beneficiary</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            )}
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Growth Assumption</Form.Label>
                  <Form.Select
                    value={String(form.growthAssumption || 5)}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        growthAssumption: +e.target.value,
                      }))
                    }
                  >
                    <option value="0">0%</option>
                    <option value="3">3%</option>
                    <option value="5">5%</option>
                    <option value="7">7%</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </>
        )}

        {/* Withholding defaults */}
        <hr />
        <h6>Default Withholding Rates</h6>
        <Row className="mb-3">
          <Col md={4}>
            <Form.Group>
              <Form.Label>Federal Withholding %</Form.Label>
              <Form.Control
                type="number"
                min="0"
                max="100"
                step="0.01"
                placeholder="e.g., 22"
                isInvalid={
                  form.defaultWithholdingRateFed != null &&
                  (form.defaultWithholdingRateFed < 0 ||
                    form.defaultWithholdingRateFed > 100)
                }
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    defaultWithholdingRateFed: +e.target.value,
                  }))
                }
              />
              <Form.Control.Feedback type="invalid">
                Must be between 0 and 100.
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>State Withholding %</Form.Label>
              <Form.Control
                type="number"
                min="0"
                max="100"
                step="0.01"
                placeholder="e.g., 5"
                isInvalid={
                  form.defaultWithholdingRateState != null &&
                  (form.defaultWithholdingRateState < 0 ||
                    form.defaultWithholdingRateState > 100)
                }
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    defaultWithholdingRateState: +e.target.value,
                  }))
                }
              />
              <Form.Control.Feedback type="invalid">
                Must be between 0 and 100.
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        {memberNeedsDob && !memberBirthYear && (
          <span
            className="text-warning me-auto"
            style={{ fontSize: "0.85rem" }}
          >
            Save the date of birth above before creating the profile
          </span>
        )}
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!canSave}>
          Create RMD Profile
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const LogDistributionModal = ({
  show,
  onHide,
  annualId,
  onSave,
}: {
  show: boolean;
  onHide: () => void;
  annualId: number | null;
  onSave: (data: CreateRMDDistribution) => Promise<void>;
}) => {
  const [form, setForm] = useState<Partial<CreateRMDDistribution>>({
    grossAmount: 0,
    isQcd: false,
    fedWithholding: 0,
    stateWithholding: 0,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (
      !annualId ||
      !form.distributionDate ||
      !form.grossAmount ||
      form.grossAmount <= 0
    )
      return;
    setSaving(true);
    try {
      await onSave({
        ...form,
        rmdAnnualId: annualId,
      } as CreateRMDDistribution);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Log Distribution</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Distribution Date</Form.Label>
          <Form.Control
            type="date"
            max={new Date().toLocaleDateString("en-CA")}
            onChange={(e) =>
              setForm((f) => ({ ...f, distributionDate: e.target.value }))
            }
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Gross Amount</Form.Label>
          <Form.Control
            type="number"
            min="0.01"
            step="0.01"
            onChange={(e) =>
              setForm((f) => ({ ...f, grossAmount: +e.target.value }))
            }
          />
        </Form.Group>
        <Row className="mb-3">
          <Col>
            <Form.Group>
              <Form.Label>Federal Withholding</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                onChange={(e) =>
                  setForm((f) => ({ ...f, fedWithholding: +e.target.value }))
                }
              />
            </Form.Group>
          </Col>
          <Col>
            <Form.Group>
              <Form.Label>State Withholding</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    stateWithholding: +e.target.value,
                  }))
                }
              />
            </Form.Group>
          </Col>
        </Row>
        <Form.Group className="mb-3">
          <Form.Check
            type="checkbox"
            label="This is a Qualified Charitable Distribution (QCD)"
            onChange={(e) =>
              setForm((f) => ({ ...f, isQcd: e.target.checked }))
            }
          />
        </Form.Group>
        {form.isQcd && (
          <Form.Group className="mb-3">
            <Form.Label>Charity / QCD Notes</Form.Label>
            <Form.Control
              placeholder="e.g., American Red Cross"
              value={form.notes ?? ""}
              onChange={(e) =>
                setForm((f) => ({ ...f, notes: e.target.value || null }))
              }
            />
          </Form.Group>
        )}
        <Form.Group className="mb-3">
          <Form.Label>Source / Description</Form.Label>
          <Form.Control
            placeholder="e.g., Bond maturity, Schwab auto-distribution"
            onChange={(e) =>
              setForm((f) => ({ ...f, sourceDescription: e.target.value }))
            }
          />
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={
            saving ||
            !form.distributionDate ||
            !form.grossAmount ||
            form.grossAmount <= 0
          }
        >
          {saving ? "Saving..." : "Save Distribution"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
